import { useEffect, useState, type ReactNode } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import type { EnterpriseAppCredentialsStatus } from "@dosia/shared";
import { apiCall } from "../../lib/api-client.js";
import { ApiError, canManageOrg, type CloudFeishuAppStatus } from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { integrationsPath, cloudFeishuAppPath } from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { StatusPill } from "../../components/StatusPill.js";
import { FormField, PrimaryButton, TextInput } from "../../components/FormField.js";
import type { OrgDetailContext } from "./context.js";

/**
 * 集成与凭据 — 企业管理员在管理后台配置组织级 app 凭证。
 * dosia-enterprise 服务间通道透传（/integrations，org-admin + super/support 可管理）。
 * 业务单一真相源在 dosia-enterprise，本页只做状态渲染。
 *
 * **联网搜索 / 音频转写的两张厂商密钥卡已随 REQ-268 撤掉**：两项能力改由网关按 gateway
 * 组托管，超管改在「工具模型策展」为 `tavily` / `volc_asr` 填组名，这里不再保管厂商密钥。
 *
 * **飞书组织配置不在本页**：飞书 bundle 读取管理员本机飞书登录态（桌面强绑），
 * 需在桌面端 DOSIA 完成，此处只给一条说明。
 *
 * 凭证 bundle 的数据形状是跨端序列化结构（红线 #9），typed 定义在 @dosia/shared，
 * 本页 import 复用而非另 declare。
 */

type StatusResponse = EnterpriseAppCredentialsStatus & { channelDisabled?: boolean };

/** 保存成功 → "已下发给 N 名员工"；失败 → upstream message（如「密钥无效」）。 */
type SaveResult = { kind: "ok"; text: string } | { kind: "error"; text: string };

function SaveResultBanner({ result }: { result: SaveResult }): JSX.Element {
  if (result.kind === "error") {
    return <ErrorBanner>{result.text}</ErrorBanner>;
  }
  return (
    <div className="rounded-md border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-700">
      {result.text}
    </div>
  );
}

/** 已配置 ✓ / 未配置 状态徽标。 */
function ConfiguredPill({ configured }: { configured: boolean }): JSX.Element {
  return configured ? (
    <StatusPill status="connected" label="已配置 ✓" />
  ) : (
    <StatusPill status="disconnected" label="未配置" />
  );
}

function CredentialCard({
  title,
  hint,
  badge,
  children,
}: {
  title: string;
  hint: ReactNode;
  badge: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-medium text-dosia-text">{title}</h3>
        {badge}
      </div>
      <p className="text-xs text-dosia-text-secondary leading-relaxed">{hint}</p>
      {children}
    </div>
  );
}

// ---------- 云端执行应用卡片 ----------

/**
 * 云端定时任务执行飞书操作所用的应用身份（机器人）。员工把表格/文档/群聊共享给这个机器人后，
 * 云端「关机照跑」定时任务即可用它的 tenant 身份代跑飞书动作（不同于桌面端"员工扫码授权"路径，
 * 两者并行不互相替代）。appId 明文展示，appSecret 只写不回显；服务端自有 Mongo 加密存储
 * （与上方 dosia-enterprise 透传通道不同，见 lib/api-paths.ts cloudFeishuAppPath 注释）。
 */
function CloudFeishuAppCard({
  orgCode,
  token,
  status,
  onSaved,
}: {
  orgCode: string;
  token: string;
  status: CloudFeishuAppStatus;
  onSaved: () => void;
}): JSX.Element {
  const [appId, setAppId] = useState(status.appId ?? "");
  const [appSecret, setAppSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SaveResult | null>(null);

  // status 首次到达 / 刷新后同步 appId 输入（appSecret 永不回填，留空=保留原值）。
  useEffect(() => {
    setAppId(status.appId ?? "");
  }, [status.appId]);

  const canSave =
    appId.trim().length > 0 && (status.hasSecret || appSecret.trim().length > 0) && !saving;

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    setResult(null);
    try {
      await apiCall<CloudFeishuAppStatus>("PUT", cloudFeishuAppPath(orgCode), {
        token,
        body: {
          appId: appId.trim(),
          ...(appSecret.trim() ? { appSecret: appSecret.trim() } : {}),
        },
      });
      setResult({ kind: "ok", text: "已保存。agent-host 下次拉取时生效（≤ 缓存 TTL）。" });
      setAppSecret("");
      onSaved();
    } catch (err) {
      setResult({
        kind: "error",
        text: err instanceof ApiError ? err.message : "保存失败，请稍后重试。",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <CredentialCard
      title="云端执行应用"
      badge={<ConfiguredPill configured={status.hasSecret} />}
      hint={
        <>
          云端定时任务执行飞书操作所用的应用身份（机器人）。录入应用凭证（appId / appSecret）后，
          员工把要用的多维表格/文档/群聊共享给这个机器人，即可让定时任务在员工关机后照常运行。
          App Secret 为写入项，保存后不回显。
        </>
      }
    >
      <div className="space-y-3">
        {result && <SaveResultBanner result={result} />}
        <FormField label="App ID">
          <TextInput
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            placeholder="cli_..."
            className="font-mono"
          />
        </FormField>
        <FormField
          label="App Secret"
          hint={status.hasSecret ? "已配置（留空则保留原值，输入新值替换）" : "首次配置请填写"}
        >
          <TextInput
            type="password"
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            placeholder={status.hasSecret ? "已配置，留空保留" : "保存后不回显"}
            autoComplete="off"
            className="font-mono"
          />
        </FormField>
        <PrimaryButton disabled={!canSave} onClick={() => void save()}>
          {saving ? "保存中…" : "保存"}
        </PrimaryButton>
      </div>
    </CredentialCard>
  );
}

// ---------- 页面 ----------

export function IntegrationsPanel(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  // org-admin 管自己 org，super/support 代任意组织配置（canManageOrg 集中派生）。
  const canManage = canManageOrg(auth?.scope);
  const statusQ = useApiQuery<StatusResponse>(
    canManage ? integrationsPath(org.code) : null,
    [org.code, canManage],
  );
  // 云端执行应用走 admin-portal 自有存储（非 dosia-enterprise 透传通道），独立查询——
  // 不受上面 channelDisabled（dosia-enterprise 通道）影响。
  const cloudFeishuAppQ = useApiQuery<CloudFeishuAppStatus>(
    canManage ? cloudFeishuAppPath(org.code) : null,
    [org.code, canManage],
  );

  const header = (
    <div>
      <h2 className="text-lg font-semibold">集成与凭据</h2>
      <p className="text-sm text-dosia-text-secondary">
        组织级集成状态与云端执行应用配置。联网搜索、音频转文字等能力的厂商密钥已由网关统一托管——在「平台工具策展」为对应能力填写网关组名即可，本页不再录入任何厂商密钥。
      </p>
    </div>
  );

  const feishuNote = (
    <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
      <span className="font-medium text-dosia-text">飞书组织配置</span>
      ：需在桌面端 DOSIA 完成（读取本机飞书登录态），此处不提供。
    </div>
  );

  const tencentNote = (
    <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
      <span className="font-medium text-dosia-text">腾讯文档</span>
      ：已改为员工各自在客户端授权（去 docs.qq.com 官方 MCP 授权页登录 QQ/微信复制 token，粘贴到
      DOSIA 设置里即可），无需管理员在此配置。
    </div>
  );

  return (
    <div className="space-y-4 max-w-2xl">
      {header}

      {!canManage ? (
        <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          请先登录管理员账号后配置组织级凭据。
        </div>
      ) : statusQ.data?.channelDisabled ? (
        <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          集成与凭据通道未配置，请联系 DOSIA 运营。
        </div>
      ) : (
        <>
          {statusQ.error && <ErrorBanner>{statusQ.error.message}</ErrorBanner>}
          {statusQ.loading && !statusQ.data ? (
            <p className="text-sm text-dosia-text-dim">加载中…</p>
          ) : statusQ.data ? (
            <>
              {tencentNote}
            </>
          ) : null}
        </>
      )}

      {canManage && (
        <>
          {cloudFeishuAppQ.error && <ErrorBanner>{cloudFeishuAppQ.error.message}</ErrorBanner>}
          {cloudFeishuAppQ.loading && !cloudFeishuAppQ.data ? (
            <p className="text-sm text-dosia-text-dim">加载中…</p>
          ) : cloudFeishuAppQ.data && auth ? (
            <CloudFeishuAppCard
              orgCode={org.code}
              token={auth.token}
              status={cloudFeishuAppQ.data}
              onSaved={() => void cloudFeishuAppQ.refetch()}
            />
          ) : null}
        </>
      )}

      {feishuNote}
    </div>
  );
}
